import * as React from 'react'
import { CircuitBoard as CircuitBoardIcon } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  useGetPlantPlcNodeDetailQuery,
  useGetPlantPlcTreeQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { Empty } from '../../components/primitives/Empty'
import { plantPlcIndexHref } from '../../lib/plant-links'
import { PlcDetailPanel } from './PlcDetailPanel'
import { PlcTreeNav } from './PlcTreeNav'

/**
 * `/plant/plc` — PLC tab.
 *
 * Single page for both unselected (empty pane on the right) and selected
 * (`?node=<id>`) states. The left rail and the right detail panel are
 * always mounted so tree expand/collapse state survives intra-tab
 * navigation — same shape as the Processes tab.
 *
 * URL contract: the selected PLC node lives in `?node=` and is shared
 * across Controllers, Programs, Routines, AOIs, and PLCTags. See
 * `plantHref` for the canonical entity link.
 */
const PlcPage = (): React.ReactElement => {
  const [searchParams] = useSearchParams()
  const rawParam = searchParams.get('node')
  // URN-style ids round-trip with encodeURIComponent in `plantHref`;
  // decode here so the detail query sees the raw graph id.
  const selectedNodeId = rawParam ? decodeURIComponent(rawParam) : null

  const tree = useGetPlantPlcTreeQuery()
  const nodes = tree.data?.nodes ?? []
  const totalTagCount = tree.data?.totalTagCount ?? 0

  const detail = useGetPlantPlcNodeDetailQuery(selectedNodeId ?? '', {
    skip: !selectedNodeId
  })

  const breadcrumbName = React.useMemo(() => {
    if (!detail.data) return selectedNodeId ?? ''
    switch (detail.data.kind) {
      case 'Controller':
        return detail.data.controller.name
      case 'Program':
        return detail.data.program.name
      case 'Routine':
        return detail.data.routine.name
      case 'AOI':
        return detail.data.aoi.name
      case 'PLCDataType':
        return detail.data.dataType.name
      case 'PLCDataTypeMember':
        return detail.data.member.name
      case 'PLCTag':
        return detail.data.tag.name
    }
  }, [detail.data, selectedNodeId])

  return (
    <div className="flex flex-1 flex-col gap-card">
      {selectedNodeId ? (
        <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
          <Link to={plantPlcIndexHref()} className="hover:text-ink hover:underline">
            PLC
          </Link>
          <span className="mx-1.5 text-ink-4">/</span>
          <span className="text-ink">{breadcrumbName}</span>
        </nav>
      ) : (
        <header data-tour-id="plant-plc-header">
          <h1 className="m-0 text-2xl font-semibold text-ink">PLC</h1>
          <p className="mt-1 text-sm text-ink-3">{plcSubtitle(nodes, totalTagCount)}</p>
        </header>
      )}

      <div
        data-tour-id="plant-plc-surface"
        className="grid grid-cols-1 gap-4 lg:grid-cols-[17.5rem_minmax(0,1fr)]"
      >
        <PlcTreeNav
          nodes={nodes}
          isLoading={tree.isLoading}
          selectedId={selectedNodeId ?? undefined}
        />
        <div className="flex min-w-0 flex-col gap-card">
          {selectedNodeId ? (
            <PlcDetailPanel nodeId={selectedNodeId} />
          ) : (
            <div className="flex min-h-80 flex-col rounded-xl border bg-card text-card-foreground shadow-sm">
              {tree.isError ? (
                <Empty
                  title="Couldn't load PLC tree"
                  hint="Check the proxy is reachable and try again."
                  icon={CircuitBoardIcon}
                />
              ) : (
                <Empty
                  title="Select a PLC node"
                  hint="Pick a Controller, Program, Routine, AOI, or data type from the tree to see its tags, members, mappings, and cross-references."
                  icon={CircuitBoardIcon}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// The tag count comes from a separate server-side `count(t:PLCTag)`
// rather than summing the per-container HAS_TAG totals, which would
// under-report whenever tags exist without an owner edge (or with the
// edge on a node outside the four tree kinds). `nodes.length === 0`
// still gates the headline so we don't show a misleading "0 tags" line
// during the initial load.
function plcSubtitle(
  nodes: {
    kind: 'Controller' | 'Program' | 'Routine' | 'AOI' | 'PLCDataType'
    tagCount: number
  }[],
  totalTagCount: number
): string {
  if (nodes.length === 0) {
    return 'Controllers, programs, routines, AOIs, data types, and the tags that flow between them.'
  }
  let controllers = 0
  let programs = 0
  let routines = 0
  let aois = 0
  let dataTypes = 0
  for (const node of nodes) {
    if (node.kind === 'Controller') controllers += 1
    else if (node.kind === 'Program') programs += 1
    else if (node.kind === 'Routine') routines += 1
    else if (node.kind === 'AOI') aois += 1
    else if (node.kind === 'PLCDataType') dataTypes += 1
  }
  const parts: string[] = [
    `${controllers.toLocaleString()} ${controllers === 1 ? 'controller' : 'controllers'}`,
    `${programs.toLocaleString()} ${programs === 1 ? 'program' : 'programs'}`,
    `${routines.toLocaleString()} ${routines === 1 ? 'routine' : 'routines'}`,
    `${aois.toLocaleString()} ${aois === 1 ? 'AOI' : 'AOIs'}`,
    `${dataTypes.toLocaleString()} ${dataTypes === 1 ? 'data type' : 'data types'}`,
    `${totalTagCount.toLocaleString()} ${totalTagCount === 1 ? 'tag' : 'tags'}`
  ]
  return `${parts.join(' · ')}.`
}

export default PlcPage
